import React, { useState, useMemo } from 'react'
import { Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/components'
import { Button } from '@/ui/components'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components'
import { WaypointResponse, RouteResponse } from '@/shared/api/routes'
import { Tent, MapPin, Info } from '@/ui/icons'

interface CampsiteSearchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  routeSegments?: (RouteResponse & { day?: number })[] // Kept for backward compatibility but not used
  waypoints?: WaypointResponse[] // Waypoints for the trip
  tripId?: string // Trip ID to fetch waypoints if not provided
  tripName?: string // Trip name to display
  onSearch: (selection: { day?: number; routeId?: string; waypointId?: string }) => void
  isLoading?: boolean
}

export function CampsiteSearchDialog({
  open,
  onOpenChange,
  waypoints: providedWaypoints,
  tripId,
  tripName,
  onSearch,
  isLoading = false,
}: CampsiteSearchDialogProps) {
  const [selectedWaypointId, setSelectedWaypointId] = useState<string | undefined>(undefined)
  
  // Filter waypoints to only show campsite waypoints
  const campsiteWaypoints = (providedWaypoints || []).filter(
    wp => wp.poi_type === 'campsite' && wp.latitude && wp.longitude
  )

  // Group waypoints by day for better organization
  const waypointsByDay = useMemo(() => {
    const grouped = new Map<number, WaypointResponse[]>()
    
    campsiteWaypoints.forEach(wp => {
      const day = wp.trip_day || 0 // 0 for unassigned
      if (!grouped.has(day)) {
        grouped.set(day, [])
      }
      grouped.get(day)!.push(wp)
    })
    
    // Sort by day
    return Array.from(grouped.entries()).sort((a, b) => a[0] - b[0])
  }, [campsiteWaypoints])

  const handleSearch = () => {
    if (selectedWaypointId) {
      onSearch({ waypointId: selectedWaypointId })
    }
  }

  const canSearch = selectedWaypointId !== undefined

  const handleClose = () => {
    if (!isLoading) {
      setSelectedWaypointId(undefined)
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Tent className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <DialogTitle>Discover Campsites</DialogTitle>
              {tripName && (
                <div className="flex items-center gap-1.5 mt-1">
                  <MapPin className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{tripName}</span>
                </div>
              )}
            </div>
          </div>
          <DialogDescription className="pt-2">
            Select a campsite waypoint to search for dead ends nearby. We'll search within a 2.5 mile radius.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Info Banner */}
          <div className="flex items-start gap-2 p-3 bg-adventure-50 border border-adventure-200 rounded-lg">
            <Info className="h-4 w-4 text-adventure-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">
                Only waypoints labeled as "campsite" are available for discovery. Dead ends within 2.5 miles of the selected campsite will be shown.
              </p>
            </div>
          </div>

          {/* Waypoint Selection */}
          <div className="space-y-2">
            <label htmlFor="waypoint-select" className="text-sm font-medium">
              Select Campsite Waypoint
            </label>
            {campsiteWaypoints.length === 0 ? (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                No campsite waypoints found. Please label your waypoints as "campsite" to search near them.
              </div>
            ) : (
              <Select
                value={selectedWaypointId}
                onValueChange={setSelectedWaypointId}
                disabled={isLoading}
              >
                <SelectTrigger id="waypoint-select">
                  <SelectValue placeholder="Choose a campsite waypoint" />
                </SelectTrigger>
                <SelectContent>
                  {waypointsByDay.map(([day, waypoints]) => (
                    <React.Fragment key={day}>
                      {waypointsByDay.length > 1 && (
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                          {day === 0 ? 'Unassigned' : `Day ${day}`}
                        </div>
                      )}
                      {waypoints.map((waypoint) => (
                        <SelectItem key={waypoint.id} value={waypoint.id}>
                          <div className="flex flex-col">
                            <span className="font-medium">{waypoint.name}</span>
                            {waypoint.trip_day && waypointsByDay.length === 1 && (
                              <span className="text-xs text-gray-500">Day {waypoint.trip_day}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSearch}
            disabled={!canSearch || isLoading}
          >
            {isLoading ? 'Searching...' : 'Search'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

